import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import generatePath from '~settings/routing';
import * as landlordQueries from '~settings/queries';
import { fetch } from '~helpers/graphql';
import endpoints from '~settings/endpoints';
import { DeleteOutlined, FormOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Popconfirm, message, Modal } from 'antd';
import SvgFormat from '~helpers/svgFormat';
import event from '~helpers/mixpanel';
import { deleteWithdrawalMethod } from '~actions/withdrawal';
import { withdrawalMethodIcons } from '~helpers/withdrawal';
import WithdrawalEmpty from '~images/withdrawal-empty.png';

const mapStateToProps = state => {
  const list = state.withdrawal.getIn(['list']);
  return {
    list: (list && list.toJS()) || [],
  };
};
const mapDispatchToProps = dispatch => ({
  deleteWithdrawalMethod: list => {
    dispatch(deleteWithdrawalMethod(list));
  },
});
@connect(mapStateToProps, mapDispatchToProps)
@withRouter
@withTranslation()
export default class WithdrawalList extends React.Component {
  constructor() {
    super();

    this.state = {
      viewProperties: null,
    };
  }

  componentDidMount() {
    event('Viewed Payouts Page');
  }

  editWithdrawal = id => {
    this.props.history.push(generatePath('withdrawal.edit', { id }));
  };

  createWithdrawal = () => {
    this.props.history.push(generatePath('withdrawal.create'));
  };

  deleteWithdrawal = (id, index) => {
    fetch({
      url: endpoints.deleteWithdrawalMethod.url(),
      query: landlordQueries.deleteWithdrawalMethod({ input: { id } }),
    })
      .then(() => {
        this.props.deleteWithdrawalMethod(index);
        message.success(this.props.t('landlord.withdrawal.create.delete_success'));
      })
      .catch(e => {
        if (
          (e &&
            e.message &&
            e.message.includes('INVALID_INPUT') &&
            e.message.includes('does not exist')) ||
          (e && e.includes('INVALID_INPUT'))
        ) {
          message.error(
            this.props.t('landlord.withdrawal.create.error.withdrawal_has_been_deleted'),
          );
        } else {
          message.error(this.props.t('landlord.withdrawal.create.error'));
        }
      });
  };

  showViewProperties = properties => {
    this.setState({
      viewProperties: properties,
    });
  };

  render() {
    const { list, t } = this.props;
    return (
      <div className="withdrawal-list">
        <div className="withdrawal-list__header">
          <div>
            <div className="withdrawal-list__title">{t('landlord.withdrawal.title')}</div>
            <If condition={!!list && list.length !== 0}>
              <p className="withdrawal-list__tips">{t('landlord.withdrawal.tips')}</p>
            </If>
          </div>
          <If condition={!!list && list.length !== 0}>
            <Button
              onClick={this.createWithdrawal}
              type="primary"
              className="withdrawal-list__header-btn"
            >
              <PlusOutlined className="table-list__btn-icon" />
              {t('landlord.withdrawal.create')}
            </Button>
          </If>
        </div>

        <Choose>
          <When condition={!list || list.length === 0}>
            <div className="withdrawal-list__empty">
              <img
                src={WithdrawalEmpty}
                className="withdrawal-list__empty-image"
                alt={WithdrawalEmpty}
              />
              <div className="withdrawal-list__empty-text">
                {t('landlord.withdrawal.list.empty')}
              </div>
              <Button
                onClick={this.createWithdrawal}
                type="primary"
                size="large"
                className="withdrawal-list__empty-btn"
              >
                {t('landlord.withdrawal.create')}
              </Button>
            </div>
          </When>
          <Otherwise>
            <div className="withdrawal-list__list">
              <For each="item" of={list} index="idx">
                <div className="withdrawal-list__item" key={item.id}>
                  <div className="withdrawal-list__item-name">{item.name}</div>
                  <div className="withdrawal-list__item-content">
                    <div className="withdrawal-list__item-type">
                      <SvgFormat
                        className="withdrawal-list__item-type-icon"
                        type={withdrawalMethodIcons[item.methodType]}
                      />
                      <div className="withdrawal-list__item-type-text">
                        {t(`landlord.withdrawal.create.${item.methodType.toLowerCase()}.title`)}
                      </div>
                    </div>
                    <div className="withdrawal-list__item-properties">
                      <span className="withdrawal-list__item-properties-title">
                        {t('landlord.withdrawal.applied_properties.title')}
                      </span>
                      <div className="withdrawal-list__item-properties-list">
                        {`${t('landlord.withdrawal.applied_properties.count', {
                          count: item.properties.length,
                        })} `}
                        <a
                          onClick={() => {
                            this.showViewProperties(item.properties);
                          }}
                          role="presentation"
                        >
                          {t('landlord.withdrawal.applied_properties.view')}
                        </a>
                      </div>
                    </div>
                    <div className="withdrawal-list__item-modify">
                      <div className="withdrawal-list__item-modify-title">
                        {t('landlord.withdrawal.last_modified.title')}
                      </div>
                      <div className="withdrawal-list__item-modify-text">{`${
                        item.updatedBy.firstName
                      } ${item.updatedBy.lastName} ${moment(item.updatedAt).format(
                        'DD/MM/YYYY',
                      )}`}</div>
                    </div>
                    <div className="withdrawal-list__item-action">
                      <FormOutlined
                        className="withdrawal-list__item-action-icon"
                        onClick={() => {
                          this.editWithdrawal(item.id);
                        }}
                      />
                      <Popconfirm
                        title={t('landlord.withdrawal.delete.confirm')}
                        placement="topLeft"
                        overlayStyle={{ maxWidth: '350px' }}
                        onConfirm={() => {
                          this.deleteWithdrawal(item.id, idx);
                        }}
                        okText={t('landlord.payment.popup.delete_popup.yes')}
                        arrowPointAtCenter
                        cancelText={t('landlord.payment.popup.delete_popup.no')}
                      >
                        <DeleteOutlined className="withdrawal-list__item-action-icon" />
                      </Popconfirm>
                    </div>
                  </div>
                </div>
              </For>
            </div>
          </Otherwise>
        </Choose>
        <Modal
          title={t('landlord.withdrawal.applied_properties.title')}
          visible={!!this.state.viewProperties}
          onCancel={() => {
            this.setState({ viewProperties: null });
          }}
          footer={null}
          centered
          wrapClassName="small-ant-modal"
        >
          <If condition={this.state.viewProperties}>
            <For each="property" of={this.state.viewProperties} index="idx">
              <div className="withdrawal-list__modal-property" key={property.slug}>
                {property.name}
              </div>
            </For>
          </If>
        </Modal>
      </div>
    );
  }
}

WithdrawalList.propTypes = {
  list: PropTypes.array,
  t: PropTypes.func,
  history: PropTypes.object.isRequired,
  deleteWithdrawalMethod: PropTypes.func,
};

WithdrawalList.defaultProps = {
  list: null,
  t: () => {},
  deleteWithdrawalMethod: () => {},
};
